import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";

import Backdrop from "@mui/material/Backdrop";
import Toolbar from "@mui/material/Toolbar";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";

import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import DesignStage from "pages/de/styles/detail/components/DesignStage";
import DevelopStage from "pages/de/styles/detail/components/DevelopStage";
import ReferencesStage from "pages/de/styles/detail/components/ReferencesStage";

import api from "services/api";

import toast from "react-hot-toast";

import moment from "moment";
import breakpoints from "assets/theme/base/breakpoints";
import colors from "assets/theme/base/colors";

import TabPanel from "components/UpStyled/TabPanel";

const StyleDetailModal = ({ visible, handleClose, handleOk, styleId, projectId = null }) => {
  const [detail, setDetail] = useState([]);
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);

  const [uploading, setUploading] = useState(false);

  const user = useContext(AccountContext);

  useEffect(() => {
    retrieveStyleDetail(styleId, projectId);
  }, []);

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
             The event listener that's calling the handleTabsOrientation function when resizing the window.
            */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const retrieveStyleDetail = async (styleId, projectId) => {
    try {
      if (styleId && !projectId) {
        const { data } = await api.products.listStyles({ id: styleId });
        const products = data["products"] ? data["products"] : [];
        if (products.length > 0) {
          setDetail(products[0]);
        }
      } else if (styleId && projectId) {
        const { data } = await api.project.get(projectId);
        const project = data["project"] ? data["project"] : {};
        if (!project?.products) {
          project.products = [];
        }
        const product = project.products.find((x) => x.id == styleId);
        setDetail(product);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const handleRefresh = () => {
    retrieveStyleDetail(styleId, projectId);
  };

  return (
    <Dialog
      open={visible}
      onClose={(e, reason) => {
        if (reason != "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen
    >
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" color="info">
          Uploading supporting files...
        </SuiTypography>
      </Backdrop>
      <AppBar sx={{ position: "relative", zIndex: 3 }}>
        <Toolbar sx={{ backgroundColor: "#1975d2", color: "white" }}>
          <SuiTypography sx={{ ml: 2, flex: 1 }} variant="h5" component="div" color="white">
            Style Detail
          </SuiTypography>

          <IconButton edge="start" color="white" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <SuiBox sx={{ px: 3 }}>
          {detail.id ? (
            <div>
              <SuiBox display="flex" justifyContent="flex-start" alignItems="center" pb={1}>
                <SuiTypography variant="h5" textTransform="none">
                  {detail.title}
                </SuiTypography>
              </SuiBox>

              <AppBar position="sticky" sx={{ top: "1%", zIndex: 3 }} id="back-to-top-anchor">
                <Tabs
                  orientation={tabsOrientation}
                  value={tabValue}
                  onChange={handleSetTabValue}
                  sx={{ margin: "auto" }}
                >
                  <Tab label="Design" {...a11yProps(0)} sx={{ width: 240 }} />
                  <Tab label="Tech Pack" {...a11yProps(1)} sx={{ width: 240 }} />
                  <Tab label="References" {...a11yProps(2)} sx={{ width: 240 }} />
                </Tabs>
              </AppBar>

              <div>
                <TabPanel value={tabValue} index={0}>
                  <DesignStage
                    detail={detail}
                    handleRefresh={handleRefresh}
                    accountType={user.account_type}
                    handleNext={() => setTabValue(1)}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <DevelopStage detail={detail} handleRefresh={handleRefresh} />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  <ReferencesStage
                    detail={detail}
                    handleRefresh={handleRefresh}
                    accountType={user.account_type}
                  />
                </TabPanel>
              </div>
            </div>
          ) : null}
        </SuiBox>
      </DialogContent>
    </Dialog>
  );
};

export default StyleDetailModal;
